import type {
	ApiCollectionResponse,
	ApiUnit,
	ApiFloorPlan,
	ApiFloor,
	ApiLeaseTermsResponse,
	ApiUnitDetailResponse,
	ApiPlanDetailResponse,
	ApiFloorsResponse,
	ApiPortfolioPropertiesResponse,
	ApiPortfolioUnitDetailResponse,
	SpacesConfig
} from '@/types/api'
import { getConfig, getPageConfig } from '@/data/config'
import { decodeEntitiesDeep } from '@/utils/decodeEntities'

const PER_PAGE = 8

export class ApiError extends Error {
	constructor (message: string, public readonly status: number) {
		super(message)
		this.name = 'ApiError'
	}
}

async function readJson<T> (response: Response): Promise<T> {
	return decodeEntitiesDeep(await response.json() as T)
}

function getRestUrl (): string {
	return getConfig().restUrl ?? '/wp-json/spaces/v1/'
}

function getAssetId (): string {
	return getConfig().engrainAssetId ?? ''
}

/** A property to target explicitly instead of the active config's asset. */
export type AssetTarget = Pick<SpacesConfig, 'engrainAssetId' | 'assetId'>

function getPostIdParam (postId: number = getConfig().assetId): string {
	return postId ? `&post_id=${postId}` : ''
}

function getMarketBase (): string {
	const marketId = getPageConfig().portfolio?.marketId ?? 0
	return `${getPageConfig().restUrl ?? '/wp-json/spaces/v1/'}markets/${encodeURIComponent(marketId)}`
}

export interface CollectionFilters {
	beds?: number[]
	priceMin?: number
	priceMax?: number
	availableOn?: string
	/**
	 * CSS class filter groups for custom filters. Each group is an array of
	 * class names that must ALL match (AND). Groups are OR'd with each other
	 * and with the `beds` filter.
	 * Example: [['2bed', 'penthouse'], ['3bed']] means
	 *   (has 2bed AND penthouse) OR (has 3bed)
	 */
	cssClassGroups?: string[][]
	/**
	 * Custom CMS-defined filters keyed by filter slug.
	 * Values are arrays of selected option values (dot-joined CSS class chains).
	 * AND between filters, OR within each filter's selected options.
	 */
	customFilters?: Record<string, string[]>
}

function appendFilters (url: string, filters?: CollectionFilters): string {
	if (!filters) return url
	const sep = () => url.includes('?') ? '&' : '?'
	if (filters.beds && filters.beds.length > 0) {
		url += `${sep()}beds=${filters.beds.map(b => encodeURIComponent(b)).join(',')}`
	}
	if (filters.priceMin != null) {
		url += `${sep()}price_min=${encodeURIComponent(filters.priceMin)}`
	}
	if (filters.priceMax != null) {
		url += `${sep()}price_max=${encodeURIComponent(filters.priceMax)}`
	}
	if (filters.availableOn) {
		url += `${sep()}available_on=${encodeURIComponent(filters.availableOn)}`
	}
	if (filters.cssClassGroups && filters.cssClassGroups.length > 0) {
		// Encode groups as pipe-delimited, classes within a group as comma-delimited.
		// Example: [['2bed', 'penthouse'], ['3bed']] → "2bed,penthouse|3bed"
		const encoded = filters.cssClassGroups
			.map(group => group.map(c => encodeURIComponent(c)).join(','))
			.join('|')
		url += `${sep()}css_class_groups=${encoded}`
	}
	if (filters.customFilters) {
		// Each filter slug becomes one custom_filters[] param.
		// Selected values are comma-separated within each param.
		// Example: { views: ['view-city', 'view-mountain'], features: ['renovated'] }
		//   → custom_filters[]=view-city,view-mountain&custom_filters[]=renovated
		for (const selected of Object.values(filters.customFilters)) {
			if (selected.length > 0) {
				const encoded = selected.map(v => encodeURIComponent(v)).join(',')
				url += `${sep()}custom_filters[]=${encoded}`
			}
		}
	}
	return url
}

export async function fetchUnits (
	page: number,
	perPage: number = PER_PAGE,
	sort?: string,
	filters?: CollectionFilters,
	signal?: AbortSignal
): Promise<ApiCollectionResponse<ApiUnit>> {
	const base = getRestUrl()
	const assetId = getAssetId()
	let url = `${base}assets/${encodeURIComponent(assetId)}/units?page=${page}&per_page=${perPage}${getPostIdParam()}`
	if (sort) url += `&sort=${encodeURIComponent(sort)}`
	url = appendFilters(url, filters)

	const response = await fetch(url, { signal })

	if (!response.ok) {
		throw new ApiError(`Failed to fetch units: ${response.status}`, response.status)
	}

	return readJson<ApiCollectionResponse<ApiUnit>>(response)
}

export async function fetchPlans (
	page: number,
	perPage: number = PER_PAGE,
	sort?: string,
	filters?: CollectionFilters,
	signal?: AbortSignal
): Promise<ApiCollectionResponse<ApiFloorPlan>> {
	const base = getRestUrl()
	const assetId = getAssetId()
	let url = `${base}assets/${encodeURIComponent(assetId)}/plans?page=${page}&per_page=${perPage}${getPostIdParam()}`
	if (sort) url += `&sort=${encodeURIComponent(sort)}`
	url = appendFilters(url, filters)

	const response = await fetch(url, { signal })

	if (!response.ok) {
		throw new ApiError(`Failed to fetch plans: ${response.status}`, response.status)
	}

	return readJson<ApiCollectionResponse<ApiFloorPlan>>(response)
}

export async function fetchLeaseTerms (
	unitId: string,
	date: string
): Promise<ApiLeaseTermsResponse> {
	const base = getRestUrl()
	const assetId = getAssetId()
	const url = `${base}assets/${encodeURIComponent(assetId)}/units/${encodeURIComponent(unitId)}/lease-terms?date=${encodeURIComponent(date)}${getPostIdParam()}`

	const response = await fetch(url)

	if (!response.ok) {
		throw new ApiError(`Failed to fetch lease terms: ${response.status}`, response.status)
	}

	return readJson<ApiLeaseTermsResponse>(response)
}

export async function fetchUnitDetail (
	unitId: string
): Promise<ApiUnitDetailResponse> {
	const base = getRestUrl()
	const assetId = getAssetId()
	const postId = getPostIdParam().slice(1)
	const url = `${base}assets/${encodeURIComponent(assetId)}/units/${encodeURIComponent(unitId)}${postId ? `?${postId}` : ''}`

	const response = await fetch(url)

	if (!response.ok) {
		throw new ApiError(`Failed to fetch unit detail: ${response.status}`, response.status)
	}

	return readJson<ApiUnitDetailResponse>(response)
}

export async function fetchPlanDetail (
	planId: string
): Promise<ApiPlanDetailResponse> {
	const base = getRestUrl()
	const assetId = getAssetId()
	const postId = getPostIdParam().slice(1)
	const url = `${base}assets/${encodeURIComponent(assetId)}/plans/${encodeURIComponent(planId)}${postId ? `?${postId}` : ''}`

	const response = await fetch(url)

	if (!response.ok) {
		throw new ApiError(`Failed to fetch plan detail: ${response.status}`, response.status)
	}

	return readJson<ApiPlanDetailResponse>(response)
}

export async function fetchSightmapIds (
	filters?: CollectionFilters,
	asset?: AssetTarget,
	signal?: AbortSignal
): Promise<(string | number)[]> {
	const base = getRestUrl()
	const assetId = asset?.engrainAssetId ?? getAssetId()
	const postId = getPostIdParam(asset?.assetId).slice(1)
	let url = `${base}assets/${encodeURIComponent(assetId)}/sightmap-ids${postId ? `?${postId}` : ''}`
	url = appendFilters(url, filters)

	const response = await fetch(url, { signal })

	if (!response.ok) {
		throw new ApiError(`Failed to fetch sightmap IDs: ${response.status}`, response.status)
	}

	const json = await readJson<{ data: (string | number)[] }>(response)
	return json.data
}

export async function fetchFloors (asset?: AssetTarget): Promise<ApiFloor[]> {
	const base = getRestUrl()
	const assetId = asset?.engrainAssetId ?? getAssetId()
	const postId = getPostIdParam(asset?.assetId).slice(1)
	const url = `${base}assets/${encodeURIComponent(assetId)}/floors${postId ? `?${postId}` : ''}`

	const response = await fetch(url)

	if (!response.ok) {
		throw new ApiError(`Failed to fetch floors: ${response.status}`, response.status)
	}

	const json = await readJson<ApiFloorsResponse>(response)
	return json.data
}

/* ---------- Portfolio (market-scoped) ---------- */

export async function fetchPortfolioUnits (
	page: number,
	perPage: number = PER_PAGE,
	sort?: string,
	filters?: CollectionFilters,
	property?: string,
	signal?: AbortSignal,
	/** Restricts results to these properties; ignored when `property` is set. */
	properties?: string[]
): Promise<ApiCollectionResponse<ApiUnit>> {
	let url = `${getMarketBase()}/units?page=${page}&per_page=${perPage}`
	if (sort) url += `&sort=${encodeURIComponent(sort)}`
	if (property) url += `&property=${encodeURIComponent(property)}`
	else if (properties?.length) url += `&properties=${properties.map(encodeURIComponent).join(',')}`
	url = appendFilters(url, filters)

	const response = await fetch(url, { signal })

	if (!response.ok) {
		throw new ApiError(`Failed to fetch portfolio units: ${response.status}`, response.status)
	}

	return readJson<ApiCollectionResponse<ApiUnit>>(response)
}

export async function fetchPortfolioProperties (
	filters?: CollectionFilters,
	signal?: AbortSignal
): Promise<ApiPortfolioPropertiesResponse> {
	const url = appendFilters(`${getMarketBase()}/properties`, filters)

	const response = await fetch(url, { signal })

	if (!response.ok) {
		throw new ApiError(`Failed to fetch portfolio properties: ${response.status}`, response.status)
	}

	return readJson<ApiPortfolioPropertiesResponse>(response)
}

export async function fetchPortfolioUnitDetail (
	unitId: string
): Promise<ApiPortfolioUnitDetailResponse> {
	const response = await fetch(`${getMarketBase()}/units/${encodeURIComponent(unitId)}`)

	if (!response.ok) {
		throw new ApiError(`Failed to fetch portfolio unit detail: ${response.status}`, response.status)
	}

	return readJson<ApiPortfolioUnitDetailResponse>(response)
}
